import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import type { FileCustom } from "./interfaces";
import { FileRow } from "./upload-modal/fileRow";
import ButtonCircle from "../../../shared/components/button_circle";

interface ConsolidateFileDropSectionProps {
  title: string;
  hint: string;
  formats?: string[];
  files: FileCustom[];
  onAdd: (files: File[]) => void;
  onRemove: (file: FileCustom) => void;
  accept?: Record<string, string[]>;
  disabled?: boolean;
  onClear?: () => void;
  required?: boolean;
  optional?: boolean;
  size?: "compact" | "expanded";
}

export function toFileCustom(file: File, typeOfFile: FileCustom["typeOfFile"]): FileCustom {
  return {
    ...file,
    fileName: file.name,
    fileType: file.type,
    fileSize: `${(file.size / 1024).toFixed(2)} KB`,
    status: "No Cargado",
    file,
    typeOfFile,
  };
}

export function ConsolidateFileDropSection({
  title,
  hint,
  formats = [],
  files,
  onAdd,
  onRemove,
  accept,
  disabled,
  onClear,
  required,
  optional,
  size = "expanded",
}: ConsolidateFileDropSectionProps) {
  const onDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length) onAdd(accepted);
    },
    [onAdd],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    disabled,
    accept,
  });

  return (
    <div className={`consolidate-drop-section consolidate-drop-section--${size}${required ? " consolidate-drop-section--required" : ""}${optional ? " consolidate-drop-section--optional" : ""}`}>
      <div className="consolidate-drop-section__head">
        <div className="consolidate-drop-section__title-row">
          <span className="consolidate-drop-section__title">{title}</span>
          {required && <span className="consolidate-drop-section__badge consolidate-drop-section__badge--required">Requerido</span>}
          {optional && <span className="consolidate-drop-section__badge consolidate-drop-section__badge--optional">Opcional</span>}
          {files.length > 0 && (
            <span className="consolidate-drop-section__count">{files.length}</span>
          )}
        </div>
        <div className="Upload_files_container_custom_input_buttons_right">
          {onClear && files.length > 0 && (
            <ButtonCircle icon="/assets/icons/clean.svg" iconHeight={20} iconWidth={20} onClick={onClear} />
          )}
          <ButtonCircle
            icon="/assets/icons/attach_file.svg"
            iconHeight={20}
            iconWidth={20}
            onClick={open}
          />
        </div>
      </div>

      <div
        {...getRootProps()}
        className={
          "consolidate-drop-section__zone"
          + (isDragActive ? " consolidate-drop-section__zone--drag" : "")
          + (files.length > 0 ? " consolidate-drop-section__zone--filled" : "")
        }
        onClick={open}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") open(); }}
      >
        <input {...getInputProps()} />
        {files.length > 0 ? (
          <div className="consolidate-drop-section__list">
            {files.map((file, index) => (
              <FileRow key={`${file.fileName}-${index}`} file={file} onRemove={onRemove} />
            ))}
          </div>
        ) : (
          <div className="consolidate-drop-section__empty">
            <img src="/assets/icons/upload_icon.svg" width={size === "compact" ? 40 : 52} height={size === "compact" ? 40 : 52} alt="" />
            <span className="consolidate-drop-section__hint">{hint}</span>
            {formats.length > 0 && (
              <div className="consolidate-drop-section__formats">
                {formats.map((fmt) => (
                  <span key={fmt} className="consolidate-drop-section__format">{fmt}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
